import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Search, Loader2, School, Users, GraduationCap, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Schools = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    principal_name: '',
    principal_contact: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const response = await schoolsAPI.getAll();
      setSchools(response.data);
    } catch (error) {
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('School name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedSchool) {
        await schoolsAPI.update(selectedSchool.id, formData);
        toast.success('School updated successfully');
      } else {
        await schoolsAPI.create(formData);
        toast.success('School created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save school');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      principal_name: '',
      principal_contact: '',
    });
    setSelectedSchool(null);
  };

  const openEditDialog = (school, e) => {
    e.stopPropagation();
    setSelectedSchool(school);
    setFormData({
      name: school.name,
      address: school.address || '',
      principal_name: school.principal_name || '',
      principal_contact: school.principal_contact || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (school, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${school.name}?`)) return;

    try {
      await schoolsAPI.delete(school.id);
      toast.success('School deleted successfully');
      loadSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete school');
    }
  };

  const filteredSchools = schools.filter(school =>
    school.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Schools</h1>
          <p className="text-slate-500">Manage partner schools and their students</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-school-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add School
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <School className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{schools.length}</p>
              <p className="text-sm text-slate-500">Total Schools</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {schools.reduce((sum, s) => sum + (s.student_count || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Total Students</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <GraduationCap className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {schools.reduce((sum, s) => sum + (s.teacher_count || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Total Teachers</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search schools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-schools"
            />
          </div>
        </CardContent>
      </Card>

      {/* Schools Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="sticky left-0 bg-slate-50">School Name</TableHead>
                  <TableHead>Principal</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-center">Students</TableHead>
                  <TableHead className="text-center">Teachers</TableHead>
                  {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSchools.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                      No schools found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSchools.map((school) => (
                    <TableRow
                      key={school.id}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => navigate(`/schools/${school.id}`)}
                      data-testid={`school-row-${school.id}`}
                    >
                      <TableCell className="sticky left-0 bg-white">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <School className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{school.name}</p>
                            {school.address && (
                              <p className="text-sm text-slate-500 truncate max-w-[200px]">{school.address}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-slate-900">{school.principal_name || '-'}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-slate-600">{school.principal_contact || '-'}</p>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{school.student_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{school.teacher_count || 0}</Badge>
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => openEditDialog(school, e)}
                              data-testid={`edit-school-${school.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={(e) => handleDelete(school, e)}
                              data-testid={`delete-school-${school.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedSchool ? 'Edit School' : 'Add New School'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>School Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter school name"
                data-testid="school-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="School address"
              />
            </div>
            <div className="space-y-2">
              <Label>Principal Name</Label>
              <Input
                value={formData.principal_name}
                onChange={(e) => setFormData({ ...formData, principal_name: e.target.value })}
                placeholder="Principal's name"
              />
            </div>
            <div className="space-y-2">
              <Label>Principal Contact</Label>
              <Input
                value={formData.principal_contact}
                onChange={(e) => setFormData({ ...formData, principal_contact: e.target.value })}
                placeholder="Phone or email"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-school-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedSchool ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schools;
